
<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useProductOverview } from "@/api/composables/useProductOverview";
import { useProductOverviewUI } from './composables/useProductOverviewUI';
import { useProductOverviewActions } from './composables/useProductOverviewActions';
import { useProductOverviewCategories } from './composables/useProductOverviewCategories';
import SearchList from "@/vue_components/product-overview/searchlist/search-list.vue";
import AlphabetList from "@/vue_components/product-overview/aplhabet-list/alphabet-list.vue";
import SearchResultItem from "@/vue_components/product-overview/search-result-item/search-result-item.vue";
import SearchHistoryService from '@/services/searchHistoryService';

/**
 * Hauptkomponente für die Produktübersicht
 * Verwaltet die verschiedenen Anzeigemodi (alphabetisch, Liste, Suche)
 * und koordiniert die Interaktion zwischen den Unterkomponenten
 */

const {
  allProducts,
  displayedProducts,
  filteredByLetter,
  letterIndex,
  selectedLetter,
  searchKeyword,
  currentCount,
  totalCount,
  categories,
  loadMore,
  setLetter,
  setSearch,
  resetToDefault,
  loadProducts,
  loading,
} = useProductOverview();

const {
  searchType,
  labelMore,
  labelAlphabetisch,
  labelListe,
  labelKeyword,
  labelSuche,
  loadDictionary
} = useProductOverviewUI();

const {
  openCategories,
  categoriesWithProducts,
  toggleCategory,
  isCategoryOpen
} = useProductOverviewCategories(allProducts, categories, searchType);

const {
  onSearch,
  onSelectLetter,
  renderAlphabetisch,
  renderListe,
  onReadMore,
  onOrder,
  onPflichttext,
  reloadSearchHistory
} = useProductOverviewActions(
    searchType,
    selectedLetter,
    openCategories,
    setSearch,
    setLetter,
    toggleCategory,
    resetToDefault,
    loadProducts
);

/**
 * Berechnet die anzuzeigenden Produkte basierend auf aktuellem Kontext
 */
const productsToShow = computed(() => {
  if (searchType.value === 'liste') {
    return [];
  }

  if (selectedLetter.value && selectedLetter.value.length > 0) {
    return filteredByLetter.value;
  }

  // if (searchKeyword.value && searchKeyword.value.length > 0) {
  //   return displayedProducts.value;
  // }

  return displayedProducts.value;
});

/**
 * Prüft ob der "Mehr laden" Button angezeigt werden soll
 */
const canLoadMore = computed(() => {
  return searchType.value === 'alphabetisch' &&
      !searchKeyword.value &&
      !selectedLetter.value &&
      currentCount.value < totalCount.value;
});

/**
 * Bestimmt Sichtbarkeit der verschiedenen UI-Elemente
 */
const showAlphabetFilter = computed(() => searchType.value === 'alphabetisch');
const showCategoryList = computed(() => searchType.value === 'liste');
const showProductGrid = computed(() => searchType.value === 'alphabetisch' || searchType.value === 'default');

onMounted(async ()=>{
  loadDictionary;
  
  const hasHistoryMark = SearchHistoryService.Instance.checkHistoryMark();
  let reload=false;  
  if(hasHistoryMark){
    const history = SearchHistoryService.Instance.getSearchHistory();
    searchKeyword.value = history.searchTerm;  
    if(history.historyType.startsWith("praeparat")){
      reload = await reloadSearchHistory();}
  }
  if(!reload){
    await loadProducts();
  }
});


</script>

<template>
  <div class="suchformularProdukteContainer">
      <div class="row productSearchBar">
          <div class="col-12 element-suche-input">
              <search-list :placeholder="labelKeyword"
                           :search-label="labelSuche"
                           @search="onSearch"
                           :isContentSearch="false" />


              <div class="render-select d-inline-flex">
                  <button class="btn-render-select render-alpha"
                          :class="{ active: searchType === 'alphabetisch' }"
                          @click="renderAlphabetisch">
                      {{ labelAlphabetisch }}
                  </button>
                  <button class="btn-render-select render-list"
                          :class="{ active: searchType === 'liste' }"
                          @click="renderListe">
                      {{ labelListe }}
                  </button>
              </div>
          </div>
      </div>

    <alphabet-list
        v-if="showAlphabetFilter"
        :letters="letterIndex"
        :selected-letter="selectedLetter"
        @select-letter="onSelectLetter"
    />

    <div v-if="loading" class="text-center p-4">
      <div class="spinner-border" role="status">
        <span class="sr-only">Laden...</span>
      </div>
    </div>

    <div v-else-if="showCategoryList" class="row ganzeZeileMitAbstand productOtcCategories">
      <div class="col-12">
        <div class="accordion-custom">
          <div
              v-for="kategorie in categoriesWithProducts"
              :key="kategorie.id"
              class="card"
          >
            <div
                class="card-header"
                :class="{ 'collapsed': !isCategoryOpen(kategorie.id) }"
                @click="toggleCategory(kategorie.id)"
                style="cursor: pointer;"
            >
              <h5 class="otckategorie-line mb-0">
                <span
                    class="otcCategoryCircle"
                    :style="`background-color: #${kategorie.farbe}`"
                ></span>
                <span class="otcCategorie-name">
                  {{ kategorie.name }} ({{ kategorie.Results.length }})
                </span>
              </h5>
            </div>

            <div
                v-show="isCategoryOpen(kategorie.id)"
                class="collapse-content"
            >
              <div class="card-body">
                <div class="otcCategorySearchResults">
                  <div class="row">
                    <div
                        v-for="result in kategorie.Results"
                        :key="result.Source.id"
                        class="col-12 col-md-4 custom-mb3"
                    >
                      <div
                          class="product-item"
                          :class="result.Source.otcKategorieId">
                        <SearchResultItem
                            :product="result.Source"
                            :searchterm="searchKeyword"
                            @readmore="onReadMore"
                            @order="onOrder"
                            @pflichttext="onPflichttext"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="showProductGrid" class="row productSearchResults ganzeZeileMitAbstand">
      <div v-if="!loading && productsToShow.length === 0 && searchKeyword" class="col-12 text-center mt-5">
        <h4>Keine Ergebnisse für "{{ searchKeyword }}"</h4>
      </div>

      <div v-for="prod in productsToShow" :key="prod.id" class="col-12 col-md-4 custom-mb3">
        <div class="product-item" :class="prod.otcKategorieId">
          <SearchResultItem
              :product="prod" 
              :searchterm="searchKeyword"
              @readmore="onReadMore"
              @order="onOrder"
              @pflichttext="onPflichttext"
          />
        </div>
      </div>

      <div class="col-12 text-center mt-3" v-if="canLoadMore">
        <button
            class="btn btn-outline-primary btn-search-more"
            @click.prevent="loadMore(3)"
        >
          {{ labelMore }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>